import React from 'react';
import { Link } from 'react-router-dom';
import { EGZAMIN, GRUPY, PATENTY, PATENT_PO_SLUGU, maDzial } from '../data/patenty';
import JsonLd from '../seo/JsonLd';
import { breadcrumbJsonLd, faqJsonLd } from '../seo/siteMeta';
import Faq from './Faq';
import styles from '../css/modules/Nauka.module.css';

/**
 * Strona filarowa jednego patentu. Jeden komponent obsługuje wszystkie trzy -
 * treść bierze z src/data/patenty.js, więc dodanie kolejnego patentu nie
 * wymaga nowego pliku.
 */

const FAQ_PATENTU = patent => [
  {
    question: `Ile pytań jest na egzaminie na patent ${patent.dopelniacz}?`,
    answer: `Test składa się z ${EGZAMIN.pytan} pytań jednokrotnego wyboru. Na rozwiązanie masz ${EGZAMIN.minut} minut, a do zaliczenia potrzeba ${EGZAMIN.prog} poprawnych odpowiedzi.`,
  },
  {
    question: `Od ilu lat można zdawać na patent ${patent.dopelniacz}?`,
    answer:
      patent.slug === 'jachtowy-sternik-morski'
        ? 'Patent jachtowego sternika morskiego wymaga ukończonych 18 lat oraz udokumentowanego stażu morskiego: co najmniej 200 godzin żeglugi, w tym rejsu powyżej 100 godzin.'
        : `Do egzaminu na patent ${patent.dopelniacz} można przystąpić po ukończeniu ${patent.wiek} lat. Osoby niepełnoletnie potrzebują zgody opiekuna prawnego.`,
  },
  {
    question: `Co daje patent ${patent.dopelniacz}?`,
    answer: patent.uprawnia,
  },
  {
    question: `Kto wydaje patent ${patent.dopelniacz}?`,
    answer: `Patent wydaje ${patent.zwiazek} po zdaniu egzaminu przed komisją egzaminacyjną i opłaceniu opłaty patentowej.`,
  },
];

const PatentPage = ({ slug }) => {
  const patent = PATENT_PO_SLUGU[slug];
  if (!patent) return null;

  const faq = FAQ_PATENTU(patent);
  const grupy = GRUPY.filter(g => maDzial(slug, g.slug));
  const pozostale = PATENTY.filter(p => p.slug !== slug);

  return (
    <div className={styles.wrap}>
      <JsonLd data={breadcrumbJsonLd([
        { name: 'Strona główna', path: '/' },
        { name: patent.nazwa, path: `/${patent.slug}` },
      ])} />
      <JsonLd data={faqJsonLd(faq)} />

      <nav className={styles.breadcrumbs}>
        <Link to="/">Strona główna</Link> / {patent.nazwa}
      </nav>

      <h1 className={styles.h1}>Testy na patent {patent.dopelniacz}</h1>
      <p className={styles.lead}>
        Pytania z publikowanych zestawów egzaminacyjnych, każde z wyjaśnieniem odpowiedzi
        i podstawą prawną. Egzamin próbny odwzorowuje prawdziwy: {EGZAMIN.pytan} pytań,{' '}
        {EGZAMIN.minut} minut, próg {EGZAMIN.prog} poprawnych odpowiedzi.
      </p>

      <div className={styles.akcje}>
        <Link className={styles.cta} to={`/egzamin/${patent.slug}`}>Zacznij egzamin próbny</Link>
        <Link className={styles.ctaDrugi} to={`/nauka/${patent.slug}`}>Ucz się według działów</Link>
      </div>

      <div className={styles.fakty}>
        <div className={styles.fakt}>
          <p className={styles.faktEtykieta}>Egzamin teoretyczny</p>
          <p className={styles.faktWartosc}>{EGZAMIN.pytan} pytań / {EGZAMIN.minut} min</p>
        </div>
        <div className={styles.fakt}>
          <p className={styles.faktEtykieta}>Próg zaliczenia</p>
          <p className={styles.faktWartosc}>{EGZAMIN.prog} poprawnych</p>
        </div>
        <div className={styles.fakt}>
          <p className={styles.faktEtykieta}>Minimalny wiek</p>
          <p className={styles.faktWartosc}>{patent.wiek} lat</p>
        </div>
        <div className={styles.fakt}>
          <p className={styles.faktEtykieta}>Patent wydaje</p>
          <p className={styles.faktWartosc}>{patent.zwiazekSkrot}</p>
        </div>
      </div>

      <div className={styles.tresc}>
        <h2 className={styles.h2}>Do czego uprawnia patent {patent.dopelniacz}</h2>
        <p>{patent.uprawnia}</p>
        <p>
          Patent wydaje {patent.zwiazek}. Egzamin składa się z części teoretycznej
          (test) i praktycznej (manewry). Nasz serwis przygotowuje do części teoretycznej.
        </p>

        <h2 className={styles.h2}>Zakres materiału</h2>
        <p>
          Pytania egzaminacyjne dzielą się na działy. Poniższe proporcje odpowiadają temu,
          ile pytań z danego działu trafia do {EGZAMIN.pytan}-pytaniowego arkusza.
        </p>
      </div>

      <div className={styles.kafle}>
        {grupy.map(g => (
          <Link key={g.slug} to={`/nauka/${patent.slug}/${g.slug}`} className={styles.kafel}>
            <span className={styles.kafelUdzial}>ok. {g.udzial} pytań w arkuszu</span>
            <span className={styles.kafelNazwa}>{g.nazwa}</span>
            <span className={styles.kafelOpis}>{g.opis}</span>
          </Link>
        ))}
      </div>

      <h2 className={styles.h2}>Najczęstsze pytania</h2>
      <Faq items={faq} />

      <div className={styles.sasiedzi}>
        {pozostale.map(p => (
          <Link key={p.slug} to={`/${p.slug}`} className={styles.sasiad}>
            Testy na patent {p.dopelniacz}
          </Link>
        ))}
      </div>
    </div>
  );
};

export default PatentPage;
